import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, User, Briefcase, ArrowRight, Building2, Phone, MapPin, FileText, Building, Globe, AlertCircle, CheckCircle2 } from "lucide-react";
import CaptchaWidget from "@/components/CaptchaWidget";
import { validateEmail, validateIndianPhone, validatePassword } from "@/utils/validation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_realtor-dash-3/artifacts/r98q2q3d_Capin%20Realty%20Logo.jpg";

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [selectedRole, setSelectedRole] = useState("user");
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [homePageImage, setHomePageImage] = useState(null);
  
  // Validation states
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef(null);
  
  // Sourcing Manager specific fields
  const [sourcingType, setSourcingType] = useState("");  // 'from_developer' or 'mandate_company'
  const [projectName, setProjectName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [projectGoogleLocation, setProjectGoogleLocation] = useState("");
  
  // Developer Details
  const [developerName, setDeveloperName] = useState("");
  const [developerDetails, setDeveloperDetails] = useState("");
  const [developerAddress, setDeveloperAddress] = useState("");
  const [developerContact, setDeveloperContact] = useState("");
  const [developerGoogleLocation, setDeveloperGoogleLocation] = useState("");
  
  // Mandate Company Details
  const [mandateCompanyName, setMandateCompanyName] = useState("");
  const [mandateCompanyDetails, setMandateCompanyDetails] = useState("");
  const [mandateCompanyAddress, setMandateCompanyAddress] = useState("");
  const [mandateCompanyContact, setMandateCompanyContact] = useState("");
  
  const navigate = useNavigate();

  // Fetch site settings for home page image
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        if (response.data.home_page_image) {
          setHomePageImage(response.data.home_page_image);
        }
      } catch (error) {
        console.log("Could not fetch site settings");
      }
    };
    fetchSiteSettings();
  }, []);

  // Real-time email validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (!isLogin && value) {
      const result = validateEmail(value);
      setEmailError(result.error || "");
    } else {
      setEmailError("");
    }
  };

  // Real-time phone validation
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setContactNumber(value);
    if (value) {
      const result = validateIndianPhone(value);
      setPhoneError(result.error || "");
    } else {
      setPhoneError("");
    }
  };

  // Real-time password strength check
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (!isLogin && value) {
      const result = validatePassword(value);
      setPasswordStrength(result.strength);
      setPasswordError(result.error || "");
      setPasswordRequirements(result.requirements || {});
    } else {
      setPasswordStrength("");
      setPasswordError("");
      setPasswordRequirements({});
    }
  };

  // CAPTCHA change handler
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token || "");
    setCaptchaError("");
  };

  // CAPTCHA expired handler
  const handleCaptchaExpired = () => {
    setCaptchaToken("");
    setCaptchaError("CAPTCHA expired. Please verify again.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        toast.success("Welcome back!");
        
        const userRole = response.data.user.role;
        if (userRole === "user") {
          navigate("/properties");
        } else {
          navigate("/dashboard");
        }
      } else {
        // Validate email
        const emailResult = validateEmail(email);
        if (!emailResult.isValid) {
          toast.error(emailResult.error);
          setEmailError(emailResult.error);
          setLoading(false);
          return;
        }

        // Validate phone
        const phoneResult = validateIndianPhone(contactNumber);
        if (!phoneResult.isValid) {
          toast.error(phoneResult.error);
          setPhoneError(phoneResult.error);
          setLoading(false);
          return;
        }

        // Validate strong password
        const passwordResult = validatePassword(password);
        if (!passwordResult.isValid) {
          toast.error(passwordResult.error);
          setPasswordError(passwordResult.error);
          setLoading(false);
          return;
        }

        // Validate CAPTCHA
        if (!captchaToken) {
          toast.error("Please complete the CAPTCHA verification");
          setCaptchaError("Please verify you are not a robot");
          setLoading(false);
          return;
        }

        // Validate sourcing manager fields
        if (selectedRole === "sourcing_manager") {
          if (!contactNumber || !sourcingType || !projectName || !projectType || !projectAddress) {
            toast.error("Please fill all required fields for Sourcing Manager registration");
            setLoading(false);
            return;
          }
          if (sourcingType === "from_developer" && (!developerName || !developerContact)) {
            toast.error("Please fill developer details");
            setLoading(false);
            return;
          }
          if (sourcingType === "mandate_company" && (!mandateCompanyName || !mandateCompanyContact)) {
            toast.error("Please fill mandate company details");
            setLoading(false);
            return;
          }
        }
        
        const registrationData = {
          email,
          password,
          full_name: fullName,
          role: selectedRole,
          contact_number: phoneResult.formatted || contactNumber,
          recaptcha_token: captchaToken,
        };
        
        // Add sourcing manager specific fields
        if (selectedRole === "sourcing_manager") {
          registrationData.sourcing_type = sourcingType;
          registrationData.project_name = projectName;
          registrationData.project_type = projectType;
          registrationData.project_address = projectAddress;
          registrationData.project_google_location = projectGoogleLocation || null;
          
          if (sourcingType === "from_developer") {
            registrationData.developer_name = developerName;
            registrationData.developer_details = developerDetails || null;
            registrationData.developer_address = developerAddress || null;
            registrationData.developer_contact = developerContact;
            registrationData.developer_google_location = developerGoogleLocation || null;
          } else if (sourcingType === "mandate_company") {
            registrationData.mandate_company_name = mandateCompanyName;
            registrationData.mandate_company_details = mandateCompanyDetails || null;
            registrationData.mandate_company_address = mandateCompanyAddress || null;
            registrationData.mandate_company_contact = mandateCompanyContact;
          }
        }
        
        await axios.post(`${API}/auth/register`, registrationData);
        toast.success("Account created successfully! Please sign in.");
        setIsLogin(true);
        // Reset CAPTCHA
        recaptchaRef.current?.reset();
        setCaptchaToken("");
        setIsLogin(true);
        setPassword("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest-login`);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Welcome! Browsing as guest.");
      navigate("/properties");
    } catch (error) {
      toast.error("Failed to start guest session");
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: homePageImage 
            ? `linear-gradient(to bottom right, rgba(30, 58, 95, 0.6), rgba(16, 42, 67, 0.65)), url(${homePageImage})`
            : 'linear-gradient(to bottom right, #1e3a5f, #1e3a5f, #102a43)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#d4af37]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div>
            <img 
              src={LOGO_URL} 
              alt="CAPIN Realty" 
              className="h-20 w-auto object-contain rounded-xl shadow-2xl"
            />
          </div>

          {/* Main content */}
          <div className="space-y-8">
            <div>
              <h1 className="text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Trusted
                <br />
                <span className="text-[#d4af37]">Real Estate</span>
                <br />
                Partner in Pune
              </h1>
            </div>
            <p className="text-xl text-slate-300 max-w-md leading-relaxed">
              Premium properties, expert guidance, and seamless transactions for your dream investment.
            </p>
            
            {/* Stats */}
            <div className="flex gap-8 pt-4">
              <div>
                <p className="text-4xl font-bold text-[#d4af37]">500+</p>
                <p className="text-sm text-slate-400 mt-1">Properties Listed</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#d4af37]">200+</p>
                <p className="text-sm text-slate-400 mt-1">Happy Clients</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-[#d4af37]">15+</p>
                <p className="text-sm text-slate-400 mt-1">Years Experience</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-slate-400 text-sm">
            <p>© 2025 CAPIN Realty. All rights reserved.</p>
            <p className="mt-1">Pune, Maharashtra, India</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#f8fafc]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="CAPIN Realty" 
              className="h-16 w-auto object-contain rounded-lg shadow-lg"
            />
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-h-[90vh] overflow-y-auto custom-scrollbar">
            {/* Tabs */}
            <div className="flex mb-8 bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-3 text-sm font-semibold rounded-lg transition-all ${
                  isLogin
                    ? "bg-[#1e3a5f] text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="login-tab"
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-3 text-sm font-semibold rounded-lg transition-all ${
                  !isLogin
                    ? "bg-[#1e3a5f] text-white shadow-md"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="register-tab"
              >
                Register
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <Label htmlFor="fullName" className="text-[#1e3a5f] font-semibold">
                    Full Name *
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                      placeholder="Enter your full name"
                      required={!isLogin}
                      data-testid="fullname-input"
                    />
                  </div>
                </div>
              )}

              {!isLogin && (
                <div>
                  <Label htmlFor="contactNumber" className="text-[#1e3a5f] font-semibold">
                    Contact Number *
                  </Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="contactNumber"
                      type="tel"
                      value={contactNumber}
                      onChange={handlePhoneChange}
                      className={`pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] ${phoneError ? 'border-red-500' : ''}`}
                      placeholder="+91 98765 43210"
                      required={!isLogin}
                      data-testid="contact-input"
                    />
                    {phoneError && (
                      <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                    )}
                    {!phoneError && contactNumber && (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                    )}
                  </div>
                  {phoneError && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {phoneError}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-[#1e3a5f] font-semibold">
                  Email Address *
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    className={`pl-10 h-12 rounded-xl border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f] ${emailError ? 'border-red-500' : ''}`}
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                  {!isLogin && emailError && (
                    <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                  )}
                  {!isLogin && !emailError && email && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
                  )}
                </div>
                {!isLogin && emailError && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {emailError}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-[#1e3a5f] font-semibold">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    className="pl-10 pr-10 h-12 rounded-xl border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                    placeholder="Enter your password"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {!isLogin && password && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Password strength:</span>
                      <div className="flex gap-1">
                        <div className={`h-1.5 w-8 rounded ${passwordStrength === 'weak' ? 'bg-red-500' : passwordStrength === 'medium' ? 'bg-yellow-500' : passwordStrength === 'strong' ? 'bg-green-500' : 'bg-slate-200'}`} />
                        <div className={`h-1.5 w-8 rounded ${passwordStrength === 'medium' ? 'bg-yellow-500' : passwordStrength === 'strong' ? 'bg-green-500' : 'bg-slate-200'}`} />
                        <div className={`h-1.5 w-8 rounded ${passwordStrength === 'strong' ? 'bg-green-500' : 'bg-slate-200'}`} />
                      </div>
                      <span className={`text-xs capitalize ${passwordStrength === 'weak' ? 'text-red-500' : passwordStrength === 'medium' ? 'text-yellow-600' : passwordStrength === 'strong' ? 'text-green-600' : ''}`}>
                        {passwordStrength}
                      </span>
                    </div>
                    {/* Password Requirements Checklist */}
                    {passwordError && (
                      <div className="p-2 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs text-red-600 font-medium mb-1">Password requirements:</p>
                        <div className="grid grid-cols-2 gap-1">
                          <div className={`text-xs flex items-center gap-1 ${passwordRequirements.minLength ? 'text-green-600' : 'text-red-500'}`}>
                            {passwordRequirements.minLength ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            8+ characters
                          </div>
                          <div className={`text-xs flex items-center gap-1 ${passwordRequirements.uppercase ? 'text-green-600' : 'text-red-500'}`}>
                            {passwordRequirements.uppercase ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            Uppercase (A-Z)
                          </div>
                          <div className={`text-xs flex items-center gap-1 ${passwordRequirements.lowercase ? 'text-green-600' : 'text-red-500'}`}>
                            {passwordRequirements.lowercase ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            Lowercase (a-z)
                          </div>
                          <div className={`text-xs flex items-center gap-1 ${passwordRequirements.number ? 'text-green-600' : 'text-red-500'}`}>
                            {passwordRequirements.number ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            Number (0-9)
                          </div>
                          <div className={`text-xs flex items-center gap-1 ${passwordRequirements.special ? 'text-green-600' : 'text-red-500'}`}>
                            {passwordRequirements.special ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            Special (!@#$%)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Role selection for registration */}
              {!isLogin && (
                <div>
                  <Label htmlFor="registerAs" className="text-[#1e3a5f] font-semibold">Register As *</Label>
                  <select
                    id="registerAs"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="mt-2 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                    data-testid="role-select"
                  >
                    <option value="user">Property Seeker</option>
                    <option value="agent">Property Agent</option>
                    <option value="sourcing_manager">Sourcing Manager</option>
                  </select>
                  {/* Role Description */}
                  <div className="mt-2 p-3 bg-[#f0f4f8] rounded-lg border border-[#1e3a5f]/10">
                    <p className="text-sm text-slate-600">
                      {selectedRole === "user" && (
                        <span><strong className="text-[#1e3a5f]">Property Seeker:</strong> Browse properties, compare listings, and submit inquiries to find your perfect property.</span>
                      )}
                      {selectedRole === "agent" && (
                        <span><strong className="text-[#1e3a5f]">Property Agent:</strong> List and manage properties, handle leads, and connect with potential buyers and renters.</span>
                      )}
                      {selectedRole === "sourcing_manager" && (
                        <span><strong className="text-[#1e3a5f]">Sourcing Manager:</strong> Source new properties, manage developer relationships, and oversee property listings.</span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Sourcing Manager Additional Fields */}
              {!isLogin && selectedRole === "sourcing_manager" && (
                <div className="space-y-4 p-4 bg-[#f0f4f8] rounded-xl border border-[#1e3a5f]/20 max-h-[400px] overflow-y-auto">
                  <h4 className="text-sm font-semibold text-[#1e3a5f] flex items-center gap-2 sticky top-0 bg-[#f0f4f8] py-2">
                    <FileText className="h-4 w-4" />
                    Project Details (Required for Sourcing Manager)
                  </h4>
                  
                  {/* Sourcing Type Selection */}
                  <div>
                    <Label className="text-[#1e3a5f] font-semibold text-sm">
                      Sourcing From *
                    </Label>
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setSourcingType("from_developer")}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          sourcingType === "from_developer"
                            ? "border-[#1e3a5f] bg-[#1e3a5f] text-white"
                            : "border-slate-200 hover:border-[#1e3a5f]/50"
                        }`}
                        data-testid="sourcing-type-developer"
                      >
                        <Building2 className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">From Developer</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSourcingType("mandate_company")}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          sourcingType === "mandate_company"
                            ? "border-[#1e3a5f] bg-[#1e3a5f] text-white"
                            : "border-slate-200 hover:border-[#1e3a5f]/50"
                        }`}
                        data-testid="sourcing-type-mandate"
                      >
                        <Building className="h-5 w-5 mx-auto mb-1" />
                        <span className="text-sm font-medium">Mandate Company</span>
                      </button>
                    </div>
                  </div>

                  {/* Project Details - Always Shown */}
                  {sourcingType && (
                    <>
                      <div className="border-t border-[#1e3a5f]/10 pt-4">
                        <h5 className="text-sm font-semibold text-[#1e3a5f] mb-3">Project Information</h5>
                        
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="projectName" className="text-[#1e3a5f] font-semibold text-sm">
                              Project Name *
                            </Label>
                            <Input
                              id="projectName"
                              type="text"
                              value={projectName}
                              onChange={(e) => setProjectName(e.target.value)}
                              className="mt-1 h-10 rounded-lg border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                              placeholder="e.g., Green Valley Residency"
                              required
                              data-testid="project-name-input"
                            />
                          </div>

                          <div>
                            <Label htmlFor="projectType" className="text-[#1e3a5f] font-semibold text-sm">
                              Project Type *
                            </Label>
                            <select
                              id="projectType"
                              value={projectType}
                              onChange={(e) => setProjectType(e.target.value)}
                              className="mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
                              required
                              data-testid="project-type-select"
                            >
                              <option value="">Select Project Type</option>
                              <option value="residential">Residential</option>
                              <option value="commercial">Commercial</option>
                              <option value="mixed_use">Mixed Use</option>
                              <option value="township">Township</option>
                              <option value="plotted_development">Plotted Development</option>
                              <option value="villa_project">Villa Project</option>
                              <option value="high_rise">High Rise</option>
                            </select>
                          </div>

                          <div>
                            <Label htmlFor="projectAddress" className="text-[#1e3a5f] font-semibold text-sm">
                              Project Address *
                            </Label>
                            <div className="relative mt-1">
                              <MapPin className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                              <textarea
                                id="projectAddress"
                                value={projectAddress}
                                onChange={(e) => setProjectAddress(e.target.value)}
                                className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-200 focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] text-sm resize-none"
                                placeholder="Full project address including city, state"
                                rows={2}
                                required
                                data-testid="project-address-input"
                              />
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="projectGoogleLocation" className="text-[#1e3a5f] font-semibold text-sm">
                              Google Maps Location (Paste Link)
                            </Label>
                            <div className="relative mt-1">
                              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                id="projectGoogleLocation"
                                type="url"
                                value={projectGoogleLocation}
                                onChange={(e) => setProjectGoogleLocation(e.target.value)}
                                className="pl-10 h-10 rounded-lg border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                                placeholder="https://maps.google.com/..."
                                data-testid="project-google-location-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Developer Details - Shown when "From Developer" is selected */}
                      {sourcingType === "from_developer" && (
                        <div className="border-t border-[#1e3a5f]/10 pt-4">
                          <h5 className="text-sm font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Developer Details
                          </h5>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Developer Name *</Label>
                              <Input
                                type="text"
                                value={developerName}
                                onChange={(e) => setDeveloperName(e.target.value)}
                                className="mt-1 h-10 rounded-lg border-slate-200"
                                placeholder="Developer/Builder Name"
                                required
                                data-testid="developer-name-input"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Developer Contact *</Label>
                              <div className="relative mt-1">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                  type="tel"
                                  value={developerContact}
                                  onChange={(e) => setDeveloperContact(e.target.value)}
                                  className="pl-10 h-10 rounded-lg border-slate-200"
                                  placeholder="+91 98765 43210"
                                  required
                                  data-testid="developer-contact-input"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Company Address</Label>
                              <textarea
                                value={developerAddress}
                                onChange={(e) => setDeveloperAddress(e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                                placeholder="Developer's office address"
                                rows={2}
                                data-testid="developer-address-input"
                              />
                            </div>

                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Additional Details</Label>
                              <textarea
                                value={developerDetails}
                                onChange={(e) => setDeveloperDetails(e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                                placeholder="RERA number, past projects, etc."
                                rows={2}
                                data-testid="developer-details-input"
                              />
                            </div>

                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Google Maps Location</Label>
                              <div className="relative mt-1">
                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                  type="url"
                                  value={developerGoogleLocation}
                                  onChange={(e) => setDeveloperGoogleLocation(e.target.value)}
                                  className="pl-10 h-10 rounded-lg border-slate-200"
                                  placeholder="Developer office Google Maps link"
                                  data-testid="developer-google-location-input"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mandate Company Details - Shown when "Mandate Company" is selected */}
                      {sourcingType === "mandate_company" && (
                        <div className="border-t border-[#1e3a5f]/10 pt-4">
                          <h5 className="text-sm font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                            <Building className="h-4 w-4" />
                            Mandate Company Details
                          </h5>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Company Name *</Label>
                              <Input
                                type="text"
                                value={mandateCompanyName}
                                onChange={(e) => setMandateCompanyName(e.target.value)}
                                className="mt-1 h-10 rounded-lg border-slate-200"
                                placeholder="Mandate Company Name"
                                required
                                data-testid="mandate-company-name-input"
                              />
                            </div>
                            
                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Company Contact *</Label>
                              <div className="relative mt-1">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                  type="tel"
                                  value={mandateCompanyContact}
                                  onChange={(e) => setMandateCompanyContact(e.target.value)}
                                  className="pl-10 h-10 rounded-lg border-slate-200"
                                  placeholder="+91 98765 43210"
                                  required
                                  data-testid="mandate-company-contact-input"
                                />
                              </div>
                            </div>

                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Company Address</Label>
                              <textarea
                                value={mandateCompanyAddress}
                                onChange={(e) => setMandateCompanyAddress(e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                                placeholder="Mandate company office address"
                                rows={2}
                                data-testid="mandate-company-address-input"
                              />
                            </div>

                            <div>
                              <Label className="text-[#1e3a5f] font-semibold text-sm">Company Details</Label>
                              <textarea
                                value={mandateCompanyDetails}
                                onChange={(e) => setMandateCompanyDetails(e.target.value)}
                                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none"
                                placeholder="Registration details, services offered, etc."
                                rows={2}
                                data-testid="mandate-company-details-input"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {isLogin && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-sm text-[#1e3a5f] hover:text-[#d4af37] font-medium transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* CAPTCHA Widget - Only show for registration */}
              {!isLogin && (
                <div className="space-y-2">
                  <Label className="text-[#1e3a5f] font-semibold">
                    Verify You're Human *
                  </Label>
                  <div className="flex justify-center">
                    <CaptchaWidget
                      ref={recaptchaRef}
                      onChange={handleCaptchaChange}
                      onExpired={handleCaptchaExpired}
                    />
                  </div>
                  {captchaError && (
                    <p className="text-red-500 text-sm text-center flex items-center justify-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {captchaError}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || (!isLogin && !captchaToken)}
                className="w-full h-12 bg-gradient-to-r from-[#1e3a5f] to-[#162c46] hover:from-[#162c46] hover:to-[#102a43] text-white font-semibold rounded-xl shadow-lg shadow-[#1e3a5f]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {loading ? (
                  <div className="spinner h-5 w-5 border-white border-t-transparent" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isLogin ? "Sign In" : "Create Account"}
                    <ArrowRight className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">or</span>
              </div>
            </div>

            {/* Guest access */}
            <Button
              type="button"
              onClick={handleGuestLogin}
              disabled={guestLoading}
              variant="outline"
              className="w-full h-12 border-2 border-[#d4af37] text-[#1e3a5f] hover:bg-[#fffdf5] font-semibold rounded-xl transition-all"
              data-testid="guest-button"
            >
              {guestLoading ? (
                <div className="spinner h-5 w-5 border-[#1e3a5f] border-t-transparent" />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Building2 className="h-5 w-5 text-[#d4af37]" />
                  Browse Properties as Guest
                </span>
              )}
            </Button>

            {/* Terms */}
            <p className="mt-6 text-center text-xs text-slate-500">
              By continuing, you agree to our{" "}
              <a href="#" className="text-[#1e3a5f] hover:text-[#d4af37]">Terms of Service</a>
              {" "}and{" "}
              <a href="#" className="text-[#1e3a5f] hover:text-[#d4af37]">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
